import { AsyncQueries } from "../base";
import { AltiusRes, IPaginationResponse } from "store/types";
import { IClient, IClientFilters } from "store/types/user-management";
import { IEntity } from "components/User-management/Clients/EntitiesTab.types";
import type { IAvailableInvestor } from "store/actions/client-entity-allocation-actions/client-entity-allocation-actions";
import { ICreateAllocationPayload } from "store/actions/client-entity-allocation-actions/client-entity-allocation-actions";
import { IUpdateAllocationPayload } from "store/actions/client-entity-allocation-actions/client-entity-allocation-actions";

/**
 * Query service for client management API calls.
 * Extends AsyncQueries to inherit the shared API client.
 */
export interface IClientQueries {
  getClientsList: (filters: IClientFilters) => Promise<IPaginationResponse<IClient>>;
  // Create a new client record.
  createClient: (name: string, type?: string | null, rm?: string | null, country?: string | null, primaryCurrency?: string | null) => Promise<{ id: number }>;
  // Fetch RM list (unique distinct RM values from client_characteristic_data).
  getRMList: () => Promise<string[]>;
  // Fetch all investment entities available for linking (per-client).
  getAllEntities: (clientId: number) => Promise<IAvailableInvestor[]>;
  // Fetch entities currently linked to a specific client.
  getClientEntities: (clientId: number) => Promise<IEntity[]>;
  // Link an entity to a client.
  linkEntityToClient: (payload: ICreateAllocationPayload) => Promise<{ success: boolean }>;
  // Update an entity's allocation percentage.
  updateEntityAllocation: (allocationId: number, payload: IUpdateAllocationPayload) => Promise<{ success: boolean }>;
  // Unlink an entity from a client.
  unlinkEntityFromClient: (allocationId: number) => Promise<{ success: boolean }>;
  // Fetch a single client's details (name + characteristics).
  getClientDetail: (clientId: number) => Promise<{
    id: number;
    name: string;
    type: string | null;
    rm: string | null;
    domicile: string | null;
    primaryCurrency: string | null;
    contact: string | null;
    entities: IEntity[];
  }>;
}

export class ClientQueries extends AsyncQueries implements IClientQueries {
  public getClientsList: IClientQueries["getClientsList"] = async (filters: IClientFilters) => {
    // Strip undefined values from filters to avoid sending empty query params
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== "")
    );

    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPaginationResponse<IClient>>>({
      url: "/api/v0.0.3/clients",
      params: cleanedFilters
    });
    return data;
  };
  public createClient: IClientQueries["createClient"] = async (name, type, rm, country, primaryCurrency) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<{ id: number }>>({
      url: "/api/v0.0.3/clients",
      data: { name, type, rm, country, primary_currency: primaryCurrency },
    });
    return data;
  };

  public getRMList: IClientQueries["getRMList"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<string[]>>({
      url: "/api/v0.0.3/clients/rm-list",
    });
    return data;
  };

  public getAllEntities: IClientQueries["getAllEntities"] = async (clientId: number) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IAvailableInvestor[]>>({
      url: `/api/v0.0.3/client-entity-allocations/available-investors`,
      params: { client_id: clientId }
    });

    return data ?? [];
  }

  /**
   * Fetch the list of available entity rows for a client's allocation table.
   */
  public getClientEntities: IClientQueries["getClientEntities"] = async (clientId: number) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IEntity[]>>({
      url: `/api/v0.0.3/client-entity-allocations`,
      params: { client_id: clientId }
    });

    return data ?? [];
  }

  public linkEntityToClient: IClientQueries["linkEntityToClient"] = async (payload) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<{ success: boolean }>, ICreateAllocationPayload>({
      url: `/api/v0.0.3/client-entity-allocations`,
      data: payload
    });
    return data;
  }

  public updateEntityAllocation: IClientQueries["updateEntityAllocation"] = async (allocationId, payload) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<{ success: boolean }>, IUpdateAllocationPayload>({
      url: `/api/v0.0.3/client-entity-allocations/${allocationId}`,
      data: payload
    });
    return data;
  }

  public unlinkEntityFromClient: IClientQueries["unlinkEntityFromClient"] = async (allocationId: number) => {
    const {
      data: { data }
    } = await this.api.delete<AltiusRes<{ success: boolean }>>({
      url: `/api/v0.0.3/client-entity-allocations/${allocationId}`
    });
    return data;
  }

  public getClientDetail: IClientQueries["getClientDetail"] = async (clientId: number) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<{
      id: number;
      name: string;
      type: string | null;
      rm: string | null;
      domicile: string | null;
      primaryCurrency: string | null;
      contact: string | null;
      entities: IEntity[];
    }>>({
      url: `/api/v0.0.3/clients/${clientId}`,
    });
    return data;
  }
}